import type { RewardConfig, RewardSnapshot } from '@contracts';
import { RewardIcon } from '@ui/shared/icons/RewardIcon';
import { resolveLevelIdentity, resolveNextLevelIdentity } from './levelProgress';

/**
 * `LevelFooterChip` (settings-rewards.md SS2, SS8 component inventory) — the
 * Today-view footer's level-identity treatment: icon tile + name only, an
 * UNLABELED pulse-line for position. Deliberately the quietest rendering of
 * level identity anywhere in the product (SS2.1): no level number, no icon
 * fill/inversion (stays surface.hover/text.secondary — the neutral tile
 * treatment, not the Rewards-tab's vivid "you are here" row), no numeric
 * fraction or countdown sentence anywhere in the visible text — the binding
 * ambient-not-literal anti-pattern (PRD SS8 v0.10, tokens.md SS9).
 *
 * Accessibility does NOT inherit "ambient" as an excuse to hide the fact
 * (today-view.md SS5's own established rule, restated for this chip in
 * settings-rewards.md SS9): aria-label states the full fact even though the
 * visual is deliberately ambient.
 */
export function LevelFooterChip({
  snapshot,
  config,
}: {
  readonly snapshot: RewardSnapshot;
  readonly config: RewardConfig;
}) {
  const { identity, fractionToNext } = resolveLevelIdentity(snapshot, config);
  const next = resolveNextLevelIdentity(snapshot, config);
  const xpToNext = Math.max(0, snapshot.xpRequiredForNextLevel - snapshot.xpIntoCurrentLevel);

  const ariaLabel =
    next && next.name !== identity.name
      ? `Level ${snapshot.level}, ${identity.name}, ${snapshot.xpIntoCurrentLevel} of ${snapshot.xpRequiredForNextLevel} XP to ${next.name}`
      : `Level ${snapshot.level}, ${identity.name}, ${xpToNext} XP to next level`;

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '24px',
          height: '24px',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--surface-hover)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 'none',
        }}
      >
        <RewardIcon iconId={identity.iconId} size={14} color="var(--text-secondary)" />
      </span>
      <span
        aria-hidden="true"
        style={{
          fontSize: 'var(--type-caption-size)',
          fontWeight: 500,
          color: 'var(--text-secondary)',
        }}
      >
        {identity.name}
      </span>
      <span
        aria-hidden="true"
        style={{
          height: '2px',
          width: '96px',
          borderRadius: 'var(--radius-pill)',
          background: 'var(--border-subtle)',
          overflow: 'hidden',
          flex: 'none',
        }}
      >
        <span
          style={{
            display: 'block',
            height: '100%',
            width: `${Math.round(fractionToNext * 100)}%`,
            background: 'var(--hue-primary-vivid)',
            borderRadius: 'var(--radius-pill)',
            transition: 'width var(--dur-counter) var(--ease-standard)',
          }}
        />
      </span>
    </div>
  );
}
